/**
 * Algolia Search Autocomplete Web Component
 * Provides search suggestions as user types in the header search input
 */

class AlgoliaAutocomplete extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.abortController = null;
    this.selectedIndex = -1;
    this.isOpen = false;

    // Algolia configuration
    this.algoliaAppId = 'IMQETC3TW8';
    this.algoliaApiKey = 'e67f48dc8732fc3da1d1daa31ddc45e4';
    this.algoliaIndex = 'shopify_products';

    // Category page paths - search stays on these pages instead of redirecting to /search
    this.categoryPagePaths = [
      '/pages/products-sermon-series',
      '/pages/products-sermon-calendars',
      '/pages/products-sermon-illustrations',
      '/pages/products-bible-guides',
      '/pages/products-graphics',
      '/pages/products-videos',
      '/pages/products-social',
      '/pages/products-docs-forms'
    ];
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.dropdown = this.querySelector('[data-dropdown]');
    this.suggestionsList = this.querySelector('.algolia-autocomplete__suggestions');
    this.clearButton = this.querySelector('.algolia-autocomplete__clear');
    this.form = this.querySelector('form');

    if (!this.input || !this.dropdown || !this.suggestionsList) {
      console.error('AlgoliaAutocomplete: Required elements not found');
      return;
    }

    this.setupEventListeners();
    this.updatePlaceholderFromPage();
  }

  /**
   * Check for a custom search placeholder set by the current page/section via localStorage
   */
  updatePlaceholderFromPage() {
    const customPlaceholder = localStorage.getItem('searchPlaceholder');
    if (customPlaceholder) {
      this.input.placeholder = customPlaceholder;
    }
  }

  setupEventListeners() {
    // Debounced input handler
    this.input.addEventListener('input', this.debounce(this.onInput.bind(this), 300));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    // Close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));

    // Form submission
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    // Clear button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClear.bind(this));
    }
  }

  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  async onInput(event) {
    const query = this.input.value.trim();

    // Toggle clear button visibility
    this.toggleClearButton(query.length > 0);

    if (query.length < 2) {
      this.close();
      return;
    }

    // Check cache first
    if (this.cachedResults[query]) {
      this.renderSuggestions(this.cachedResults[query]);
      this.open();
      return;
    }

    // Fetch from Algolia
    const suggestions = await this.fetchSuggestions(query);
    if (suggestions) {
      this.cachedResults[query] = suggestions;
      this.renderSuggestions(suggestions);
      this.open();
    }
  }

  async fetchSuggestions(query) {
    // Cancel any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const url = `https://${this.algoliaAppId}-dsn.algolia.net/1/indexes/${this.algoliaIndex}/query`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': this.algoliaAppId,
          'X-Algolia-API-Key': this.algoliaApiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query,
          hitsPerPage: 6,
          attributesToRetrieve: ['title', 'handle'],
          attributesToHighlight: ['title'],
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>',
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Algolia request failed: ${response.status}`);
      }

      const data = await response.json();
      return data.hits.map(hit => ({
        title: hit.title,
        highlightedTitle: hit._highlightResult?.title?.value || hit.title,
        handle: hit.handle,
      }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Algolia fetch error:', error);
      }
      return null;
    }
  }

  renderSuggestions(suggestions) {
    if (!suggestions || suggestions.length === 0) {
      this.suggestionsList.innerHTML = `
        <li class="algolia-autocomplete__no-results">
          No results found
        </li>
      `;
      return;
    }

    this.suggestionsList.innerHTML = suggestions.map((suggestion, index) => `
      <li
        class="algolia-autocomplete__suggestion"
        role="option"
        id="algolia-suggestion-${index}"
        data-index="${index}"
        data-title="${this.escapeHtml(suggestion.title)}"
        data-handle="${this.escapeHtml(suggestion.handle)}"
        aria-selected="false"
      >
        <svg class="algolia-autocomplete__suggestion-icon" aria-hidden="true" viewBox="0 0 20 20" fill="none">
          <path d="M14.386 14.386L18 18M16.667 9.333A7.333 7.333 0 1 1 2 9.333a7.333 7.333 0 0 1 14.667 0Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="algolia-autocomplete__suggestion-text">${suggestion.highlightedTitle}</span>
      </li>
    `).join('');

    // Add click handlers to suggestions
    this.suggestionsList.querySelectorAll('.algolia-autocomplete__suggestion').forEach(item => {
      item.addEventListener('click', this.onSuggestionClick.bind(this));
    });

    this.selectedIndex = -1;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  onKeydown(event) {
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' && this.input.value.trim().length >= 2) {
        this.onInput();
      }
      return;
    }

    const suggestions = this.suggestionsList.querySelectorAll('.algolia-autocomplete__suggestion');

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, suggestions.length - 1);
        this.updateSelection(suggestions);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(suggestions);
        break;

      case 'Enter':
        if (this.selectedIndex >= 0 && suggestions[this.selectedIndex]) {
          event.preventDefault();
          const handle = suggestions[this.selectedIndex].dataset.handle;
          this.close();
          // Navigate directly to the product page
          window.location.href = `/products/${handle}`;
        }
        // If no selection, let the form submit naturally
        break;

      case 'Escape':
        event.preventDefault();
        this.close();
        this.input.blur();
        break;
    }
  }

  updateSelection(suggestions) {
    suggestions.forEach((item, index) => {
      const isSelected = index === this.selectedIndex;
      item.setAttribute('aria-selected', isSelected);
      item.classList.toggle('algolia-autocomplete__suggestion--selected', isSelected);
    });

    // Update ARIA attributes on input
    if (this.selectedIndex >= 0) {
      this.input.setAttribute('aria-activedescendant', `algolia-suggestion-${this.selectedIndex}`);
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onSuggestionClick(event) {
    const item = event.currentTarget;
    const handle = item.dataset.handle;
    this.close();
    // Navigate directly to the product page
    window.location.href = `/products/${handle}`;
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query.length >= 2 && this.cachedResults[query]) {
      this.renderSuggestions(this.cachedResults[query]);
      this.open();
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onSubmit(event) {
    const query = this.input.value.trim();
    if (!query) {
      event.preventDefault();
      return;
    }

    // Check if on a category page - if so, stay on it with the search query
    const currentPath = window.location.pathname;
    if (this.categoryPagePaths.includes(currentPath)) {
      event.preventDefault();
      this.close();
      window.location.href = `${currentPath}?q=${encodeURIComponent(query)}`;
      return;
    }

    this.close();
  }

  onClear(event) {
    event.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.toggleClearButton(false);
    this.close();
  }

  toggleClearButton(show) {
    if (this.clearButton) {
      this.clearButton.classList.toggle('hidden', !show);
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.dropdown.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dropdown.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.selectedIndex = -1;
  }
}

customElements.define('algolia-autocomplete', AlgoliaAutocomplete);
